import { useState } from "react";
import { ChevronDown } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
} from "@/components/ui/dropdown-menu";
import { getOfferItems, getTrustItems, getNavItems } from "./navData";
import SiteSearch from "./SiteSearch";

const DesktopNav = () => {
  const [openSubIndex, setOpenSubIndex] = useState<number | null>(null);
  const { lang, t } = useLanguage();
  const offerItems = getOfferItems(t, lang);
  const trustItems = getTrustItems(t);
  const navItems = getNavItems(t);

  return (
    <nav className="hidden lg:flex items-center justify-between w-full gap-2">
      {navItems.map((item) =>
        item.dropdown ? (
          <DropdownMenu key={item.href} modal={false} onOpenChange={() => setOpenSubIndex(null)}>
            <DropdownMenuTrigger className="flex items-center gap-1 text-[14px] font-medium text-primary-foreground/90 hover:text-primary-foreground transition-colors outline-none">
              {item.label}
              <ChevronDown className="w-3 h-3 ml-0.5" strokeWidth={1.5} />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="min-w-[240px] rounded-lg shadow-[0_6px_20px_rgba(0,0,0,0.08)] border-border/30 p-2">
              {item.dropdown === "trust" ? (
                <>
                  <DropdownMenuLabel className="text-xs uppercase tracking-wider text-muted-foreground/70 font-semibold">Reference</DropdownMenuLabel>
                  {trustItems.reference.map((sub) => (
                    <DropdownMenuItem key={sub.href} asChild>
                      <a href={sub.href} className="cursor-pointer flex items-center gap-2">
                        <sub.icon className="w-4 h-4 text-primary/70" />{sub.label}
                      </a>
                    </DropdownMenuItem>
                  ))}
                  <DropdownMenuSeparator />
                  <DropdownMenuLabel className="text-xs uppercase tracking-wider text-muted-foreground/70 font-semibold">Testimonials</DropdownMenuLabel>
                  {trustItems.testimonials.map((sub) => (
                    <DropdownMenuItem key={sub.href} asChild>
                      <a href={sub.href} className="cursor-pointer flex items-center gap-2">
                        <sub.icon className="w-4 h-4 text-primary/70" />{sub.label}
                      </a>
                    </DropdownMenuItem>
                  ))}
                  <DropdownMenuSeparator />
                  <DropdownMenuLabel className="text-xs uppercase tracking-wider text-muted-foreground/70 font-semibold">Partners</DropdownMenuLabel>
                  {trustItems.partners.map((sub) => (
                    <DropdownMenuItem key={sub.href} asChild>
                      <a href={sub.href} className="cursor-pointer flex items-center gap-2">
                        <sub.icon className="w-4 h-4 text-primary/70" />{sub.label}
                      </a>
                    </DropdownMenuItem>
                  ))}
                </>
              ) : (
                offerItems.map((sub, subIdx) =>
                  sub.subItems && sub.subItems.length > 0 ? (
                    <DropdownMenuSub key={sub.href} open={openSubIndex === subIdx} onOpenChange={(open) => setOpenSubIndex(open ? subIdx : null)}>
                      <DropdownMenuSubTrigger className="cursor-pointer flex items-center gap-2">
                        <sub.icon className="w-4 h-4 text-primary/70" />{sub.label}
                      </DropdownMenuSubTrigger>
                      <DropdownMenuSubContent className="min-w-[280px] rounded-lg shadow-[0_6px_20px_rgba(0,0,0,0.08)] border-border/30 p-2">
                        <DropdownMenuItem asChild>
                          <a href={sub.href} className="cursor-pointer font-semibold">
                            {lang === "en" ? "All Products" : "Vsi produkti"} →
                          </a>
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        {sub.subItems.map((cat: any, ci: number) =>
                          cat.isCategory ? (
                            <div key={ci}>
                              {ci > 0 && <DropdownMenuSeparator />}
                              {cat.href ? (
                                <DropdownMenuLabel className="text-xs uppercase tracking-wider font-semibold">
                                  <a href={cat.href} className="text-primary hover:underline cursor-pointer">{cat.label}</a>
                                </DropdownMenuLabel>
                              ) : (
                                <DropdownMenuLabel className="text-xs uppercase tracking-wider text-primary font-semibold">{cat.label}</DropdownMenuLabel>
                              )}
                              {cat.subItems?.map((child: any) =>
                                child.isCategory ? (
                                  <div key={child.label}>
                                    {child.href ? (
                                      <DropdownMenuLabel className="text-sm font-normal pb-0">
                                        <a href={child.href} className="text-foreground hover:text-primary cursor-pointer">{child.label}</a>
                                      </DropdownMenuLabel>
                                    ) : (
                                      <DropdownMenuLabel className="text-sm font-normal text-foreground pb-0">{child.label}</DropdownMenuLabel>
                                    )}
                                    {child.subItems?.map((grandchild: any) => (
                                      <DropdownMenuItem key={grandchild.href} asChild>
                                        <a href={grandchild.href} className="cursor-pointer pl-4">{grandchild.label}</a>
                                      </DropdownMenuItem>
                                    ))}
                                  </div>
                                ) : (
                                  <DropdownMenuItem key={child.href} asChild>
                                    <a href={child.href} className="cursor-pointer">{child.label}</a>
                                  </DropdownMenuItem>
                                )
                              )}
                            </div>
                          ) : (
                            <DropdownMenuItem key={cat.href} asChild>
                              <a href={cat.href} className="cursor-pointer">{cat.label}</a>
                            </DropdownMenuItem>
                          )
                        )}
                      </DropdownMenuSubContent>
                    </DropdownMenuSub>
                  ) : (
                    <DropdownMenuItem key={sub.href} asChild>
                      <a href={sub.href} className="cursor-pointer flex items-center gap-2">
                        <sub.icon className="w-4 h-4 text-primary/70" />{sub.label}
                      </a>
                    </DropdownMenuItem>
                  )
                )
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        ) : (
          <a
            key={item.href}
            href={item.href}
            className="text-[14px] font-medium text-primary-foreground/90 hover:text-primary-foreground transition-colors hover:underline underline-offset-4 decoration-1"
          >
            {item.label}
          </a>
        )
      )}
      <SiteSearch />
    </nav>
  );
};

export default DesktopNav;
